import { AlertTriangle } from "lucide-react";

import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useRagCorpus, useRagOverview, type FacetRow, type RepoFreshness } from "../api";

/** Freshness thresholds, in days since the newest indexed commit. Two days
 *  covers a working nightly sync plus a weekend; past a fortnight the index is
 *  actively misleading for anything recent. */
const STALE_DAYS = 2;
const VERY_STALE_DAYS = 14;

/** The sync runs nightly, so anything past ~2 days means it isn't running. */
const SYNC_ALERT_HOURS = 48;

function freshnessTone(ageDays: number | null): "positive" | "warning" | "danger" {
  if (ageDays === null) return "danger";
  if (ageDays <= STALE_DAYS) return "positive";
  if (ageDays <= VERY_STALE_DAYS) return "warning";
  return "danger";
}

function freshnessLabel(ageDays: number | null): string {
  if (ageDays === null) return "unknown";
  if (ageDays === 0) return "today";
  if (ageDays === 1) return "1 day old";
  return `${ageDays} days old`;
}

/** 12,630,706 -> "12.6M". Plain toLocaleString would print fourteen characters
 *  into a tile built for four, and the precision is meaningless here anyway. */
function compact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

const TONE_TEXT: Record<"positive" | "warning" | "danger", string> = {
  positive: "text-success",
  warning: "text-warning",
  danger: "text-danger",
};

function FacetList({ title, rows }: { title: string; rows: FacetRow[] }) {
  const total = rows.reduce((sum, r) => sum + r.count, 0) || 1;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {rows.slice(0, 8).map((r) => (
          <div key={r.value} className="flex items-center gap-3 text-sm">
            <span className="w-28 shrink-0 truncate font-mono text-xs">{r.value}</span>
            {/* Proportion bar — width is data-driven, so it stays inline. */}
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-brand"
                style={{ width: `${Math.max(2, (r.count / total) * 100)}%` }}
              />
            </span>
            <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
              {r.count.toLocaleString()}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function RepoRow({
  repo,
  onSearchRepo,
}: {
  repo: RepoFreshness;
  onSearchRepo: (repo: string) => void;
}) {
  const tone = freshnessTone(repo.age_days);
  return (
    <TableRow>
      <TableCell className="font-medium">
        <Button
          variant="link"
          className="h-auto p-0 font-medium"
          onClick={() => onSearchRepo(repo.repo)}
        >
          {repo.repo}
        </Button>
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {repo.chunks.toLocaleString()}
      </TableCell>
      <TableCell className="tabular-nums">
        {repo.indexed_through ? repo.indexed_through.slice(0, 10) : "—"}
      </TableCell>
      <TableCell className={TONE_TEXT[tone]}>{freshnessLabel(repo.age_days)}</TableCell>
      <TableCell className="max-w-[22rem]">
        <div className="truncate text-sm" title={repo.last_commit_message ?? ""}>
          {repo.last_commit_message ?? "—"}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {repo.last_author ?? "—"}
          {repo.last_file ? ` · ${repo.last_file.split("/").slice(-1)[0]}` : ""}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function OverviewTab({ onSearchRepo }: { onSearchRepo: (repo: string) => void }) {
  const { data, isPending, error } = useRagOverview();
  // Separate query so its ~9s cold scan never blocks this tab.
  const corpus = useRagCorpus();

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Index unreachable</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
          <div className="mt-1 text-xs">
            The Qdrant collection lives on the Spark host — this usually means the
            VPN is down or the container is stopped.
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  const { health, repos, languages, chunk_types: chunkTypes, sync } = data;
  const syncStale =
    !sync.available || sync.age_hours === null || sync.age_hours > SYNC_ALERT_HOURS;

  return (
    <div className="flex flex-col gap-6">
      {syncStale ? (
        <Alert variant="danger">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>The nightly sync is not running</AlertTitle>
          <AlertDescription>
            {sync.available && sync.last_run
              ? `Last completed run was ${sync.last_run.slice(0, 10)} — ${Math.round(
                  (sync.age_hours ?? 0) / 24,
                )} days ago. `
              : "The sync job's status could not be read. "}
            Search still works, but it is answering from a snapshot: everything
            committed since then is invisible to it.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Indexed chunks"
          value={health.points.toLocaleString()}
          sub={`${repos.length} repos · ${health.branch} branch`}
        />
        <KpiTile
          label="Collection"
          value={<StatusBadge value={health.status === "green" ? "healthy" : health.status} />}
          sub={`${health.collection} · ${health.vector_size}d ${health.distance}`}
        />
        {/* Corpus size, not staleness: per-repo age is already in the table
            below and the banner covers the alarming case, whereas *how much
            code this is* is the thing people have no other way to see — and
            the reason retrieval beats pointing a large-context agent at the
            tree. Lazily loaded (see useRagCorpus), so it never delays the rest
            of this tab. */}
        <KpiTile
          label="Corpus size"
          value={
            corpus.data ? (
              `${compact(corpus.data.est_tokens)} tokens`
            ) : corpus.isError ? (
              "unavailable"
            ) : (
              <Skeleton className="h-7 w-28" />
            )
          }
          sub={
            corpus.data
              ? `${compact(corpus.data.chars)} chars · ~${corpus.data.context_windows_1m}x a 1M context window`
              : corpus.isError
                ? "corpus scan failed"
                : "measuring…"
          }
          info={
            corpus.data
              ? `${corpus.data.chars.toLocaleString()} characters across ${corpus.data.lines.toLocaleString()} lines and ${corpus.data.chunks.toLocaleString()} chunks. Tokens are estimated at ${corpus.data.chars_per_token} characters each — code tokenises denser than prose. Scanned server-side and cached for 12 hours.`
              : undefined
          }
          infoTitle="How this is measured"
        />
        <KpiTile
          label="Last sync run"
          value={
            sync.available && sync.last_run ? sync.last_run.slice(0, 10) : "unknown"
          }
          tone={syncStale ? "danger" : "positive"}
          sub={
            sync.available && sync.files_changed !== null
              ? `${sync.files_changed} files changed`
              : "status unavailable"
          }
        />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Freshness by repository</CardTitle>
          <p className="text-sm text-muted-foreground">
            Every repo is indexed from its{" "}
            <span className="font-medium text-foreground">{health.branch}</span> branch,
            so this tracks released code rather than whatever is in flight on a dev
            branch. &ldquo;Indexed through&rdquo; is the newest commit date actually
            present in the index for that repo — not when the sync last ran. Click a
            repo to search inside it.
          </p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Repository</TableHead>
                <TableHead className="text-right">Chunks</TableHead>
                <TableHead>Indexed through</TableHead>
                <TableHead>Age</TableHead>
                <TableHead>Newest indexed commit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {repos.map((r) => (
                <RepoRow key={r.repo} repo={r} onSearchRepo={onSearchRepo} />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <FacetList title="Chunks by language" rows={languages} />
        <FacetList title="Chunks by chunk type" rows={chunkTypes} />
      </div>
    </div>
  );
}
